export type StreamEvent = {
  id: string;
  event: string;
  data: unknown;
  channel: string;
  /** The publishing request's origin (the tab that caused it); absent for job-published events. */
  origin?: string;
  /** One id per write: every event one write sends, on every channel, carries the same one. */
  writeId?: string;
};
export type SystemFrame =
  | { type: "connected"; channel: string }
  | { type: "ping"; timestamp: number }
  | { type: "reconnect"; timestamp: number }
  | { type: "error"; error: string };

const STREAM_ID = /^\d+-\d+$/;
const encoder = new TextEncoder();

export const encodeFrame = (frame: StreamEvent | SystemFrame): Uint8Array =>
  encoder.encode(`data: ${JSON.stringify(frame)}\n\n`);

/**
 * Splits already-decoded text into complete `data:` payloads plus the unfinished tail. Pure.
 * Accepts "\n\n" and "\r\n\r\n" separators; skips non-`data:` lines and JSON that fails to parse.
 */
export function parseSseBuffer(buffer: string): {
  frames: unknown[];
  rest: string;
} {
  const blocks = buffer.replace(/\r\n/g, "\n").split("\n\n");
  const rest = blocks.pop() ?? "";
  const frames = blocks.flatMap((block) =>
    block
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .flatMap((line) => {
        try {
          return [JSON.parse(line.slice(5).trimStart()) as unknown];
        } catch {
          return [];
        }
      }),
  );
  return { frames, rest };
}

export function isStreamEvent(value: unknown): value is StreamEvent {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    STREAM_ID.test(v.id) &&
    typeof v.event === "string" &&
    typeof v.channel === "string"
  );
}

export function isPingFrame(value: unknown): value is { type: "ping" } {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { type?: unknown }).type === "ping"
  );
}

/** Redis stream ids `<ms>-<seq>`; BigInt because both parts can exceed 2^53. */
export function compareStreamIds(a: string, b: string): -1 | 0 | 1 {
  // Callers pass ids matching STREAM_ID, so both halves exist.
  const [am, as] = a.split("-").map(BigInt) as [bigint, bigint];
  const [bm, bs] = b.split("-").map(BigInt) as [bigint, bigint];
  if (am !== bm) return am < bm ? -1 : 1;
  if (as !== bs) return as < bs ? -1 : 1;
  return 0;
}
